import {
  HTMLProps,
  useCallback,
  useMemo,
} from 'react';

import {
  LocalVideoTrack,
  RemoteUser,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
} from 'agora-rtc-react';

import MicrophoneIndicator from '@/app/components/MicrophoneIndicator';
import NoVideoIndicator from '@/app/components/NoVideoIndicator';
import VideoDisplayTitle from '@/app/components/VideoDisplayTitle';
import { useParticipantData } from '@/app/queries/MeetingQuery/context';
import { useAppContext } from '../AppContext';

export interface VideoTrackInfo {
  uid: number,
  source: ICameraVideoTrack | IAgoraRTCRemoteUser,
  sourceType: 'local' | 'remote',
  isEnlarged: boolean,
  hasAudio: boolean,
  hasVideo: boolean,
}

export type VideoDisplayVariant = 'stage' | 'tile';

// The stage runs edge to edge under the floating header and controls: its
// microphone badge is pulled inside them and the name is dropped, since a
// full-screen view needs no label. A tile keeps both in its own corners.
const BADGE_PLACEMENT: Record<VideoDisplayVariant, { title: string, microphone: string }> = {
  stage: { title: 'hidden', microphone: 'top-20' },
  tile: { title: '', microphone: '' },
};

interface VideoDisplayProps extends Omit<HTMLProps<HTMLDivElement>, 'onClick'> {
  videoTrack: VideoTrackInfo;
  onClick(videoTrack: VideoTrackInfo): void;
  variant?: VideoDisplayVariant;
}

export default function VideoDisplay({
  onClick, videoTrack, variant = 'tile', ...divProps
}: VideoDisplayProps) {
  const { speaker, isMainVideoMirrored } = useAppContext();
  const participantData = useParticipantData(videoTrack.uid);

  const onClickTrack = useCallback(() => {
    onClick(videoTrack);
  }, [onClick, videoTrack]);

  // A remote stage is letterboxed: cropping it would cut away part of the
  // property the customer is showing. Everything else fills its box, the way a
  // camera viewfinder does.
  const playerConfig = useMemo(() => ({
    fit: variant === 'stage' && videoTrack.sourceType === 'remote'
      ? 'contain' as const
      : 'cover' as const,
    mirror: videoTrack.sourceType === 'local' && isMainVideoMirrored,
  }), [variant, videoTrack.sourceType, isMainVideoMirrored]);

  const badges = BADGE_PLACEMENT[variant];

  switch (videoTrack.sourceType) {
    case 'local':
      return (
        <>
          <VideoDisplayTitle className={badges.title} title={participantData?.name} />
          <LocalVideoTrack
            {...divProps}
            track={videoTrack.source as ICameraVideoTrack}
            play={videoTrack.hasVideo}
            videoPlayerConfig={playerConfig}
            onClick={onClickTrack}
          >
            <MicrophoneIndicator className={badges.microphone} isMicrophoneOn={videoTrack.hasAudio} />
            <NoVideoIndicator isCameraOn={videoTrack.hasVideo} />
          </LocalVideoTrack>
        </>

      )
    case 'remote':
      return (
        <>
          {!participantData?.isScreenShare && (
            <VideoDisplayTitle className={badges.title} title={participantData?.name} />
          )}
          <RemoteUser
            {...divProps}
            playbackDeviceId={speaker.deviceId}
            user={videoTrack.source as IAgoraRTCRemoteUser}
            playVideo={true}
            playAudio={true}
            videoPlayerConfig={playerConfig}
            onClick={onClickTrack}
          >
            <MicrophoneIndicator className={badges.microphone} isMicrophoneOn={videoTrack.hasAudio} />
            <NoVideoIndicator isCameraOn={videoTrack.hasVideo} />
          </RemoteUser>
        </>
      );
    default: {
      const neverType: never = videoTrack.sourceType;
      throw new TypeError(`Unhandled video type: ${neverType}`);
    }
  }
}
